// Difficulty tier classification driven by the logical solver. A puzzle's
// tier is the highest-rank technique its solve trace uses — singles only
// → easy; up through box-line → medium; up through x-wing → hard. Anything
// beyond x-wing (or unsolvable by v1's techniques) returns null so the
// carver knows to back off.

import { solve, TECHNIQUE_RANK, type Technique } from '../game/LogicalSolver.js';
import type { Difficulty, Grid } from '../game/types.js';

export type { Difficulty } from '../game/types.js';

/** Per-tier ceiling on the highest-rank technique a puzzle may require. */
export const MAX_RANK_FOR_DIFFICULTY: Readonly<Record<Difficulty, number>> = Object.freeze({
  easy: TECHNIQUE_RANK['hidden-single'],
  medium: TECHNIQUE_RANK['box-line'],
  hard: TECHNIQUE_RANK['x-wing'],
});

/** Tier ordered from easiest to hardest — used to map a rank back to a tier. */
const TIER_ORDER: readonly Difficulty[] = ['easy', 'medium', 'hard'];

/**
 * Classify a grid by the hardest technique needed to solve it.
 *
 * Returns:
 * - `'easy'` when only singles are needed (or the grid is already solved).
 * - `'medium'` when naked-pair / pointing-pair / box-line are required.
 * - `'hard'` when x-wing is required.
 * - `null` when the v1 solver can't finish the grid (out of tier range).
 */
export function rateGrid(grid: Grid): Difficulty | null {
  const result = solve(grid);
  if (!result.solved) return null;
  let maxRank = 0;
  for (const step of result.steps) {
    const r = TECHNIQUE_RANK[step.technique as Technique];
    if (r > maxRank) maxRank = r;
  }
  return tierForRank(maxRank);
}

function tierForRank(rank: number): Difficulty {
  for (const tier of TIER_ORDER) {
    if (rank <= MAX_RANK_FOR_DIFFICULTY[tier]) return tier;
  }
  // Unreachable while the solver only emits techniques up through x-wing.
  return 'hard';
}
